import { Component, Prop, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from '@/theme/Cart';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';
import { DrawerContainer } from '../Drawer/DrawerContainer';
import { render, html } from 'lit-html';
import { formatPrice } from '@/utils/products';
import { LinkedSubscriptions } from '../LinkedSubscriptions/LinkedSubscriptions';
import { StickyFooterBar } from '../StickyFooterBar/StickyFooterBar';
import type { ShopifyCartItemInput } from '@/theme/cart/types/ICartApi';


declare global {
    interface Window {
        shopUrl: string;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ReCharge: {
            ['cart_options']: CartOptions;
            customer: {
                ['default_address']: Address;
                email: string;
            };
        };
    }
}

@Component
export class SubscriptionForm extends ThemeComponent {

    @Prop({ default: {} }) freeGifts!: FreeGifts;
    @Prop({ default: false}) isAbsoluter!: boolean;
    @Prop({ default: false}) isLoggedIn!: boolean;
    @Prop({ default: false}) isAbsoluterTagged!: boolean;

    @Ref({required: false}) options!: ProductOptionPicker;
    @Ref({required: false}) actions!: HTMLDivElement;
    @Ref({required: false}) submitButton!: HTMLButtonElement[];
    @Ref({required: false}) submitButtonPrice!: HTMLSpanElement[];
    @Ref({required: false}) chargeDelayInput!: HTMLInputElement;
    @Ref({required: false}) fineprint!: HTMLDivElement;
    @Ref({required: false}) currentPriceElement!: HTMLElement[];
    @Ref({required: false}) currentDiscountedPriceElement!: HTMLElement[];
    @Ref({required: false}) flavourButton!: HTMLButtonElement[];
    @Ref() error!: HTMLElement;
    @Ref() errorMessage!: HTMLElement;

    private selectedProductId: number|null = null;
    private productFeaturesJson: string|null = null;
    private quantity: number = 1;
    private selectedVariant: ShopifyVariant|null = null;
    private drawerContainer: DrawerContainer|null = null;
    private quickAddDrawerContainer: DrawerContainer|null = null;
    private linkedSubscriptions: LinkedSubscriptions|null = null;
    private selectedOption: HTMLInputElement|null = null;
    private selectedOptionData: SelectedOptionData|null = null;

    private _onProductChange = new SimpleEvent<string>();
    public get onProductChange() {
        return this._onProductChange.asEvent();
    }

    /**
     * Initiaze compoenent.
     */
    init() {
        // Assign linked subscriptions component
        this.linkedSubscriptions = this.querySelector('linked-subscriptions');

        // Set the drawer container
        this.drawerContainer = document.querySelector('.drawer-container--side-cart');

        this.quickAddDrawerContainer = document.querySelector('.quick-add-drawer');

        // Setup event listeners
        this.setEventListeners();

        // Handle default selected flavour button
        const selectedFlavourButton = this.flavourButton.find((button) => button.classList.contains('is-active'));
        if (selectedFlavourButton) {
            this.handleSelectedFlavourVariant(selectedFlavourButton);
        }

        // Set the selected option data on page load
        this.setSelectedOptionData();
    }

    /**
     * Set event listeners.
     */
    protected setEventListeners() {
        // Listen for form submission
        this.listenSubmitClick();

        // Listen for changes to linked subscriptions
        if (this.linkedSubscriptions) {
            this.$subscribe(this.linkedSubscriptions.onRadioChange, (optionId) => {
                // Assign the selected option
                this.selectedOption = this.querySelector(`#${optionId}`) as HTMLInputElement;
                // Handle UI updates following the selection
                this.handleOptionChanged();
            });
        }

        // Listen for flavour button clicks
        this.listenForVariantFlavourClick();
    }

    setSelectedOptionData() {

        this.selectedOption =
        this.linkedSubscriptions?.querySelector(`[data-ref="radioInput"]:checked`) as HTMLInputElement;

        // Store option data attribues in an accessible object
        this.selectedOptionData = {
            frequency: this.selectedOption?.getAttribute('data-frequency') || '',
            frequencyUnit: this.selectedOption?.getAttribute('data-frequency-unit') || '',
            sellingPlan: this.selectedOption?.getAttribute('data-selling-plan') || '',
            quantity: this.selectedOption?.getAttribute('data-quantity') || '1',
            price: this.selectedOption?.getAttribute('data-price') || '',
            hasGift: this.selectedOption?.getAttribute('data-has-gift') || '',
            giftIds: this.selectedOption?.getAttribute('data-free-gifts') || '',
            flavour: this.selectedOption?.getAttribute('data-flavour') || '',
            upsellId: this.selectedOption?.getAttribute('data-upsell-id') || '',
            // featuresJson: this.selectedOption?.getAttribute('data-features-json') || '',
            sellingPlanUpgrade: this.selectedOption?.getAttribute('data-selling-plan-upgrade') || '',
            absoluterDiscountAmount: this.selectedOption?.getAttribute('data-absoluter-discount-amount') || ''
        };
    }

    handleOptionChanged() {

        this.setSelectedOptionData();

        // Get the selected flavour
        const selectedFlavourButton = this.flavourButton.find((button) => button.classList.contains('is-active'));
        if (selectedFlavourButton) {
            this.handleSelectedFlavourVariant(selectedFlavourButton);
        }

        // Update price elements
        this.updatePriceElements();
        this.updateAbsoluterPriceElements();
        this.updateSubmitButtonAttributes();
        this.updateStickyBar();
    }


    /**
     * Listen for submit button click.
     */
    protected listenSubmitClick() {

        // Listen for form submission
        if (this.submitButton) {

            for (const button of this.submitButton) {

                const inventoryQty = button.getAttribute('inventory');
                const continueSellingOFS = button.getAttribute('continue_selling_ofs');

                if (continueSellingOFS == 'false' && Number(inventoryQty) <= 0) {
                    button.disabled = true;
                    return;
                }

                this.$listen(button, 'click', async() => {

                    // Hide quick add if it is open.
                    if (this.quickAddDrawerContainer ) {
                        if (this.quickAddDrawerContainer.hasAttribute('open')) {
                            this.quickAddDrawerContainer?.close();
                        }
                    }

                    const subscription = button.getAttribute('is_subscription');

                    const quantity = button.getAttribute('quantity');

                    if (quantity) {
                        this.quantity = Number(quantity);
                    }

                    const properties: {[key: string]: string} = {};
                    let selected_value = 'onetime';

                    const mainImageUrl: string|null = button.getAttribute('main_product_image');
                    if (mainImageUrl) {
                        properties['_main_product_image'] = mainImageUrl || "";
                    }

                    const checkout_image_url: string|null = button.getAttribute('checkout_image');
                    if (checkout_image_url) {
                        properties['_checkout_image'] = checkout_image_url;
                    }

                    /* eslint-disable-next-line */
                    let subscription_selected:HTMLInputElement|null = this.querySelector('input[type="radio"]:checked');

                    /*
                        Absoluter price products are added to form inputs as a variant id within a data attribute.
                        If one exists on the selected product, we add the absoluter product to cart instead.

                        TODO This can be removed if the Absoluter solution changes to incorporate discounts
                    */

                    if (this.isAbsoluter && subscription_selected?.getAttribute('data-absoluter-variant-id')) {
                        this.selectedProductId = Number(
                            subscription_selected.getAttribute('data-absoluter-variant-id')
                        );
                    } else {
                        this.selectedProductId = Number(
                            subscription_selected?.getAttribute('data-variant-id')
                        );
                    }

                    if (subscription === 'true') {
                        if (!subscription_selected) {
                            subscription_selected = this.querySelector('input[name*="selling_plan_"]:checked');
                            this.selectedProductId = Number(subscription_selected?.getAttribute('data-variant-id'));
                        }

                        if (subscription_selected !== null) {
                            selected_value = subscription_selected.value;
                        }

                        if (selected_value == 'autodeliver' || selected_value == 'subsave') {
                            // this.checkRechargeVersions(button, properties);
                            // return;
                        }

                    } else {
                        const checkout_image_url: string|null = button.getAttribute('checkout_image');
                        properties['_checkout_image'] = checkout_image_url || "";

                        const parent_product_url: string|null = button.getAttribute('parent_product_url');
                        if (parent_product_url) {
                            properties['_parent_product_url'] = parent_product_url;
                        }

                        const linkedProductHandles: string|null = button.getAttribute('linked_product_handles');
                        if (linkedProductHandles) {
                            properties['_linked_product_handles'] = linkedProductHandles;
                        }

                        const oneTimePurchaseId: string|null = button.getAttribute('one-time-purchase-id');
                        if (oneTimePurchaseId) {
                            properties['_one_time_purchase_id'] = oneTimePurchaseId;
                        }

                        const linkedProductQuantities: string|null = button.getAttribute('linked_product_quantities');
                        if (linkedProductQuantities) {
                            properties['_linked_product_quantities'] = linkedProductQuantities;
                        }

                        const flavour: string|null = button.getAttribute('flavour');
                        if (flavour) {
                            properties['_flavour'] = flavour;
                        }
                    }

                    if (this.selectedProductId) {
                        await this.checkRechargeVersions(button, properties);
                    }
                });
            }
        }
    }

    /**
     * Check ReCharge Payments App Version.
     * - Background: In November 2020 Recharge updated the app to use shopify plans
     * instead of a separate recharger chechout. The implementation is slightly different
     * if the App was installed before/after November 2020.
     * - The below script checks for which implementation to use (rechargeCheckout or shopifyPlans)
     * - Learn more: https://support.rechargepayments.com/hc/en-us/articles/360008681834
     * @param value - selected value.
     * @returns
     */
    protected async checkRechargeVersions(button: HTMLElement, properties: {[key: string]: string}) {
        const sellingPlan:string|null = button.getAttribute('selling_plan');
        if (!sellingPlan) {
            await this.addToCart(button, this.selectedProductId, properties, null).catch((e) => {
                this.$log.error(`Add to cart failed`, { e });
            });
            return;
        }

        await this.handleAddSubscriptionProduct(button, sellingPlan);
    }

    /**
     * Handle cart item properties, then send cart data.
     * @param properties - product properties.
     * @param isSellingPlan - check if it has selling plan
     * @param sellingPlan - selling plan id.
     * @returns
     */
    async handleAddSubscriptionProduct(
        buttonType: HTMLElement,
        sellingPlan:string
    ) {
        // const chargeDelay = this.chargeDelayInput.value || "";
        // const charge_delay_experiment = buttonType.getAttribute('_charge_delay_experiment');
        const flavour = buttonType.getAttribute('_flavour');
        const freeGiftIds = buttonType.getAttribute('gift_ids');
        // const frequency = buttonType.getAttribute('frequency');
        const hide_qty = buttonType.getAttribute('_hide_qty') || "";
        // const is_subscription = buttonType.getAttribute('is_subscription');
        const itemAbsoluterStatus = buttonType.getAttribute('item_absoluter_status');
        const linkedProductHandles = buttonType.getAttribute('linked_product_handles');
        const linkedProductQuantities = buttonType.getAttribute('linked_product_quantities');
        const mainProductImage = buttonType.getAttribute('main_product_image');
        const oneTimePurchaseId = buttonType.getAttribute('one-time-purchase-id');
        const pack_size = buttonType.getAttribute('quantity');
        const parent_product_url = buttonType.getAttribute('parent_product_url');
        // const unit_type = buttonType.getAttribute('unit_type');
        const upsellId = buttonType.getAttribute('upsell_id') || "";
        const checkout_image_url = buttonType.getAttribute('checkout_image');
        const properties: Record<string, string> = {
            // '_charge_delay': chargeDelay || '0',
            // '_charge_delay_experiment': charge_delay_experiment || '',
            '_checkout_image': checkout_image_url || '',
            '_flavour': flavour || '',
            '_free_gift_ids': freeGiftIds || '',
            '_hide_qty': hide_qty || 'false',
            // '_is_subscription': is_subscription || '',
            '_linked_product_handles': linkedProductHandles || '',
            '_linked_product_quantities': linkedProductQuantities || '',
            '_main_product_image': mainProductImage || '',
            '_one_time_purchase_id': oneTimePurchaseId || '',
            '_pack_size': pack_size || '',
            '_parent_product_url': parent_product_url || '',
            // '_shipping_interval_frequency': frequency || '',
            // '_shipping_interval_unit_type': unit_type || '',
            '_upsell_id': upsellId,
        };

        /*
            Some products can have a 'pack size', which means we
            pass a line item property so that the quantity in cart
            appears as the pack size and not the real quantity
        */

        properties._item_absoluter_status = itemAbsoluterStatus || '';

        if (this.quantity) {
            properties._display_quantity = this.quantity.toString();
        }

        // if (this.selectedOptionData?.featuresJson) {
        //     properties._features = this.selectedOptionData.featuresJson;
        // }

        await this.addToCart(
            buttonType,
            this.selectedProductId,
            properties,
            sellingPlan
        ).catch((e) => {
            this.$log.error(`Add to cart failed`, { e });
        });
        
    }

    /**
     * Show error
     * @param msg - error message.
     */
    protected showError(msg: string|null): void {
        this.errorMessage.textContent = msg;
        if (msg) {
            this.error.classList.add('product-form__error--active');
        } else {
            this.error.classList.remove('product-form__error--active');
        }
    }

    /**
     * Add product to cart.
     * @param variant
     * @param properties
     * @returns
     */
    protected async addToCart(
        submitButtonType: HTMLElement,
        variant:number|null,
        properties: {[key: string]: string},
        sellingPlan:string|null
    ) {
        try {

            // Check variant exists
            if (!variant) {
                this.showError(`Please select an option.`);
                return;
            }

            // if (submitButtonType.getAttribute('features-json')) {
            //     properties['_features'] = String(submitButtonType.getAttribute('features-json'));
            // }

            // Define selling plan
            const sanitizedPlan = sellingPlan ? Number(sellingPlan) : undefined;
            const freeGifts = this.freeGifts ? Object.values(this.freeGifts).filter((item) => item) : [];

            let hasGift = false;

            let variantFormInput
            = document.querySelector(`[data-discount-variant-id="${variant}"]`) as HTMLInputElement;

            if (!variantFormInput) {
                variantFormInput
                = document.querySelector(`[data-variant-id="${variant}"]`) as HTMLInputElement;
            }

            if (submitButtonType.getAttribute('has_gift') !== 'false') {
                hasGift = true;
            }

            const productItem = {
                id: variant,
                quantity: this.quantity,
                properties: properties,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                selling_plan: sanitizedPlan
            };


            // Check if there are free gifts to add with product. Note these get removed in CartMain
            if (freeGifts.length === 0 || !hasGift || this.freeGifts == undefined) {
                // Add to cart
                await cart.asInteraction(async() => {
                    await cart.addItem(productItem).catch((event: unknown | null) => {
                        if (event instanceof Error) {
                            // this.showError(event.message);
                            window.console.warn('Filtering Error.');
                        }
                    });
                });
            } else {

                const giftIds: ShopifyCartItemInput[] = [];
                const freeGiftQty = freeGifts[0] === freeGifts[1] ? 2 : 1;

                for (const freeGiftId of freeGifts) {
                    giftIds.push({
                        id: Number(freeGiftId),
                        quantity: freeGiftQty,
                        properties: {
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            _eligibleProductId: String(variant),
                            _free_gift: 'true'
                        }
                    });
                    if (freeGiftQty === 2 ) { break; } // Exit loop when free gifts are the same.
                }

                const giftExists = cart.items.find((item) => item.variantId === variant);
                const subscription = submitButtonType.getAttribute('is_subscription');
                let includeGift = true;

                // Add free gift if enabled in 'recharge_content_configuration' metafield with 'has_gift' property.
                const hasGift = submitButtonType.getAttribute('has_gift');
                includeGift = hasGift === 'false' ? false : true;
                // Add gift item only once if it's a 'one time purchase'.
                if (giftExists && subscription === 'false') {
                    includeGift = false;
                }

                const productWithGifts = includeGift ? [productItem, ...giftIds] : [productItem];

                await cart.asInteraction(async() => {
                    await cart.addItems(productWithGifts).catch((event: unknown | null) => {
                        if (event instanceof Error) {
                            this.showError(event.message);
                        }
                    });
                });
            }

            const straightToCheckout = submitButtonType.getAttribute('straight-to-checkout') == 'true'
                ? true
                : false;

            if (straightToCheckout) {
                window.location.href = '/checkout';
            } else {
                this.drawerContainer?.open();
            }

        } catch (event) {
            this.$log.error('Cart error', {
                event
            });
            const { message } = event as Error;
            this.showError(message);
        }
    }

    updateFineprint(frequency: string) {
        if (frequency.includes('one-time')) {
            this.hideFineprintElement();
        } else {
            this.showFineprintElement();
        }
    }

    showFineprintElement() {
        if (this.fineprint) {
            this.fineprint.classList.add('product-form__fineprint--active');
        }
    }

    hideFineprintElement() {
        if (this.fineprint) {
            this.fineprint.classList.remove('product-form__fineprint--active');
        }
    }

    public updatePriceElements() {
        const moneyFormat = this.getAttribute('moneyFormat') || '£{{amount}}';
        const sideCartMain = document.querySelector('.side-cart__main');

        const hasQualifyingItem = cart.items.some((cartItem) => cartItem.properties?.['_item_absoluter_status']);
        const isAbsoluter = this.linkedSubscriptions?.getAttribute('is-absoluter') == 'true' ? true : false;

        const hasQualifyingAbsoluterItem = sideCartMain?.getAttribute('data-absoluter') === 'true';
        const activeInputAbsoluter = document.querySelector('.product-form__subscription-choice:checked');
        const hasAbsoluterDiscount = Boolean(activeInputAbsoluter?.getAttribute('data-absoluter-discount-amount'));

        for (const element of this.submitButtonPrice) {
            if (this.selectedOptionData) {

                const discountedPrice =
                    Number(this.selectedOptionData.price) -
                    Number(this.selectedOptionData?.absoluterDiscountAmount);

                // If a user is logged in, has an absoluter tag but does not have a qualifying item in cart
                if (this.isLoggedIn && this.isAbsoluterTagged && !hasQualifyingItem) {
                    render(html`
                        ${
    discountedPrice < Number(this.selectedOptionData?.price) ?
        html`<s>${formatPrice(Number(this.selectedOptionData?.price), moneyFormat)}</s>`:
        ''
}
                        ${formatPrice(discountedPrice, moneyFormat)}
                    `, element);

                // If a user is logged in, doesn't have an absoluter tag or a qualifying item in cart
                } else if (this.isLoggedIn && !this.isAbsoluterTagged && !hasQualifyingItem) {
                    render(html`${formatPrice(Number(this.selectedOptionData?.price), moneyFormat)}`, element);
                } else {

                    // If the user is logged in, isn't an absoluter but does have a qualifying item in cart
                    if (this.isLoggedIn && !isAbsoluter && hasQualifyingItem) {
                        render(
                            html`
                                ${
    discountedPrice < Number(this.selectedOptionData?.price) ?
        html`<s>${formatPrice(Number(this.selectedOptionData?.price), moneyFormat)}</s>`
        : ''
}
                                ${formatPrice(discountedPrice, moneyFormat)}
                            `, element
                        );
                    } else {
                        /*
                            If no qualifying item is in cart and the user is logged out,
                            or they are logged in and there is no qualifying item in cart
                        */
                        if (!hasQualifyingAbsoluterItem && !this.isLoggedIn || this.isLoggedIn && !hasQualifyingItem) {
                            render(html`${formatPrice(Number(this.selectedOptionData?.price), moneyFormat)}`, element);
                        } else {
                            // If the user is an absoluter and there is an absoluter discount
                            if (this.isAbsoluter && this.selectedOptionData?.absoluterDiscountAmount && discountedPrice
                                || isAbsoluter && hasAbsoluterDiscount && this.selectedOptionData && discountedPrice) {
                                render(html`
                                        ${
    discountedPrice < Number(this.selectedOptionData?.price) ?
        html`<s>${formatPrice(Number(this.selectedOptionData?.price), moneyFormat)}</s>` :
        ''
}
                                        ${formatPrice(discountedPrice, moneyFormat)}
                                    `, element);
                            } else {
                                render(
                                    html`${formatPrice(Number(this.selectedOptionData?.price), moneyFormat)}`, element
                                );
                            }
                        }
                    }
                }
            }
        }
    }

    public updateAbsoluterPriceElements() {
        const absoluterPriceElement = this.querySelectorAll('[data-ref="absoluterPriceElement"]');
        if (!this.selectedOptionData?.absoluterDiscountAmount) {
            this.hideAbsoluterBanner();
            return;
        }
        this.showAbsoluterBanner();
        const discountedPrice =
            Number(this.selectedOptionData.price) - Number(this.selectedOptionData?.absoluterDiscountAmount);
        const moneyFormat = this.getAttribute('moneyFormat') || '£{{amount}}';
        for (const element of absoluterPriceElement) {
            render(html`${formatPrice(Number(discountedPrice), moneyFormat)}`, element);
        }
    }

    public updateButtonPriceElements(price: string, absoluterPrice: string) {
        const moneyFormat = this.getAttribute('moneyFormat') || '£{{amount}}';
        for (const element of this.submitButtonPrice) {
            render(html`
                <s>${formatPrice(Number(price), moneyFormat)}</s>
                ${formatPrice(Number(absoluterPrice), moneyFormat)}
            `, element);
        }
    }

    updateSubmitButtonAttributes() {
        // Set values on the cart submit button
        if (!this.selectedOptionData || !this.submitButton) {
            return;
        }

        // If a subscription radio option was selected
        if (this.selectedOptionData) {

            // Add attributes
            for (const button of this.submitButton) {
                button.setAttribute('flavour', this.selectedOptionData.flavour);
                button?.setAttribute('frequency', this.selectedOptionData.frequency);
                button?.setAttribute('gift_ids', this.selectedOptionData.giftIds);
                button?.setAttribute('has_gift', this.selectedOptionData.hasGift);
                // button?.setAttribute('features-json', this.selectedOptionData.featuresJson);
                button.setAttribute('quantity', this.selectedOptionData.quantity);
                button.setAttribute('selling_plan',  this.selectedOptionData.sellingPlan);
                button?.setAttribute('unit_type', this.selectedOptionData.frequencyUnit);
                button.setAttribute('upsell_id', this.selectedOptionData.upsellId);
            }
        }
    }

    // Hide Absoluter Banner
    hideAbsoluterBanner() {
        const absoluterBanner = this.querySelector('.absoluter-banner');
        if (absoluterBanner) {
            absoluterBanner.classList.add('visually-hidden');
        }
    }

    // Show Absoluter Banner
    showAbsoluterBanner() {
        const absoluterBanner = this.querySelector('.absoluter-banner');
        if (absoluterBanner) {
            absoluterBanner.classList.remove('visually-hidden');
        }
    }

    // Listen for flavour variant button clicks
    listenForVariantFlavourClick() {
        if (this.flavourButton) {

            // Set checked flavour
            const checkedFlavour =
            document.querySelector<HTMLInputElement>('.radio-selection--checked .variant__flavour-radio');

            const stickyFlavourText =
             document.querySelector<HTMLSpanElement>('.product-sticky-footer__selection .flavour');

            // update sticky footer text
            stickyFlavourText && checkedFlavour
                ?  stickyFlavourText.textContent = checkedFlavour.dataset.value || ""
                : '';

            for (const button of this.flavourButton) {
                this.$listen(button, 'click', () => {
                    // eslint-disable-next-line
                    // this.handleSelectedFlavourVariant(variant, stickyFlavourText);
                    this.handleSelectedFlavourVariant(button);
                });
            }
        }
    }

    // Hnadle a change to the selected flavour variant
    handleSelectedFlavourVariant(button: HTMLButtonElement) {

        // Remove checked class from all buttons
        for (const button of this.flavourButton) {
            button.classList.remove('is-active');
        }

        // Apply checked class to clicked button
        button.classList.add('is-active');

        // Set checked flavour
        const checkedFlavour =
        document.querySelector<HTMLInputElement>('.variant__flavour-button.is-active');

        const stickyFlavourText =
        document.querySelector<HTMLSpanElement>('.product-sticky-footer__selection .flavour');

        // Update sticky footer text
        if (stickyFlavourText && checkedFlavour?.dataset.value) {
            stickyFlavourText.textContent = checkedFlavour.dataset.value;
        }

        const value = button.getAttribute('data-value');
        const title = document.querySelector('.variant__flavour-title span');

        const checkedFrequencyInput = document.querySelector<HTMLInputElement>(
            '.product-form__subscription-choice:checked'
        );

        const frequency = checkedFrequencyInput?.getAttribute('data-frequency');

        // Update title
        title ? title.textContent = button.getAttribute('data-value') : null;

        // Update sticky footer text
        // stickyFlavourText ? stickyFlavourText.textContent = value : null;

        const flavourJsonData =
            JSON.parse(button?.getAttribute(`data-frequency-${frequency}`) || '');

        checkedFrequencyInput?.setAttribute('data-variant-id', flavourJsonData[0].flavour_id);
        checkedFrequencyInput?.setAttribute('data-discount-variant-id', flavourJsonData[0].discount_id);
    }

    // Update the sticky footer bar state
    updateStickyBar() {
        const stickyFooterBar = document.querySelector('sticky-footer-bar') as StickyFooterBar;
        if (stickyFooterBar && this.selectedOption) {
            stickyFooterBar.updateSelectedOption(this.selectedOption.id);
        }
    }
}